import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { LogOut, Loader2, Camera, ShieldCheck, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, uploadAPI, mediaUrl } from '../services/api';

const GOALS = ['perdre du poids', 'retrouver de l’énergie', 'mieux manger', 'reprendre une routine', 'bouger davantage', 'être accompagné(e)', 'participer en équipe'];

export default function Profile() {
  const { user, updateLocalUser, logout, isCoach } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', level: 'debutant', goal: 'mieux manger', hide_leaderboard: false });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    userAPI.profile().then((u) => {
      updateLocalUser(u);
      setForm({ name: u.name || '', level: u.level || 'debutant', goal: u.goal || 'mieux manger', hide_leaderboard: !!u.hide_leaderboard });
    }).catch(() => {});
  }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const u = await userAPI.update(form);
      updateLocalUser(u);
      toast.success('Profil mis à jour');
    } catch { toast.error('Échec de la mise à jour'); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      const u = await userAPI.update({ avatar: res.url });
      updateLocalUser(u);
      toast.success('Photo mise à jour');
    } catch { toast.error("Échec de l'envoi"); }
    finally { setUploading(false); }
  };

  const doLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="space-y-6 animate-rise max-w-lg mx-auto">
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">Mon profil</h1>

      <Card className="p-6 rounded-2xl border-red-100 dark:border-red-900/50 flex flex-col items-center">
        <div className="relative">
          <Avatar className="w-24 h-24 ring-4 ring-red-100 dark:ring-red-800">
            <AvatarImage src={mediaUrl(user?.avatar)} />
            <AvatarFallback className="bg-red-600 text-white text-2xl">{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <label className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center cursor-pointer" data-testid="avatar-upload-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
          </label>
        </div>
        <p className="mt-4 font-bold text-red-950 dark:text-red-50">{user?.name}</p>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
        {isCoach && <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-300"><ShieldCheck className="w-3.5 h-3.5" />Coach</span>}
      </Card>

      <Card className="p-6 rounded-2xl border-red-100 dark:border-red-900/50 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="pname">Nom complet</Label>
          <Input id="pname" data-testid="profile-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <Label>Niveau</Label>
          <div className="flex gap-2 p-1 bg-muted rounded-full">
            {[['debutant', 'Débutant'], ['avance', 'Avancé']].map(([v, l]) => (
              <button key={v} type="button" data-testid={`profile-level-${v}`} onClick={() => setForm((f) => ({ ...f, level: v }))}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${form.level === v ? 'bg-white dark:bg-red-900 text-red-700 dark:text-red-200 shadow-sm' : 'text-muted-foreground'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pgoal">Objectif principal</Label>
          <select id="pgoal" data-testid="profile-goal" value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
            {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div>
            <p className="text-sm font-semibold text-foreground">Profil privé</p>
            <p className="text-xs text-muted-foreground">Ne pas apparaître dans le classement public</p>
          </div>
          <Switch checked={form.hide_leaderboard} onCheckedChange={(v) => setForm((f) => ({ ...f, hide_leaderboard: v }))} data-testid="hide-leaderboard-switch" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-profile" className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl py-6">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
        </Button>
      </Card>

      <Link to="/photos" data-testid="profile-photos-link" className="block">
        <div className="w-full rounded-xl border border-red-100 dark:border-red-900/50 p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
          <div className="w-10 h-10 rounded-xl bfc-gradient-warm text-white flex items-center justify-center"><Camera className="w-5 h-5" /></div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">📸 Photos avant / après</p>
            <p className="text-xs text-muted-foreground">Compare ta transformation (privé)</p>
          </div>
        </div>
      </Link>

      <Link to="/reglement" data-testid="profile-reglement-link" className="block">
        <div className="w-full rounded-xl border border-red-100 dark:border-red-900/50 p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
          <div className="w-10 h-10 rounded-xl bfc-gradient-warm text-white flex items-center justify-center"><ScrollText className="w-5 h-5" /></div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">Règlement du challenge</p>
            <p className="text-xs text-muted-foreground">Consulte les règles du BFC21</p>
          </div>
        </div>
      </Link>

      <Button variant="outline" onClick={doLogout} data-testid="profile-logout" className="w-full rounded-xl border-red-200 text-red-600 hover:bg-red-50 font-semibold">
        <LogOut className="w-4 h-4 mr-2" />Se déconnecter
      </Button>

      <p className="text-[11px] text-muted-foreground text-center leading-relaxed px-4">
        Ce challenge est un programme d’accompagnement bien-être. Il ne remplace pas un avis médical.
      </p>
    </div>
  );
}
